
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { RolesUser } from '../../enum/roles-user.enum';
import { Observable, of } from 'rxjs';
import { catchError, filter, first, map, timeout } from 'rxjs/operators';
import { SettingsService } from '../settings/settings.service';
import { AppState } from './../../interfaces/app-state.interface';
import { User } from './../../models/User';

@Injectable({ providedIn: 'root' })
export class Icvm617Service {

    private readonly maritalStatus = {
        casadoComBrasileiroNato: 5,
        casadoComBrasileiroNaturalizado: 6,
        uniaoEstavel: 8,
    };

    private readonly maritalStatusForVerification = [
        this.maritalStatus.casadoComBrasileiroNato,
        this.maritalStatus.casadoComBrasileiroNaturalizado,
        this.maritalStatus.uniaoEstavel,
    ];

    /**
     * É necessário inserir a seguinte configuração nos enviroments, onde a data
     * é o dia limite para atualização dos dados.
     * O bloqueio é feito de acordo com o último dígito do CBLC do usuário
     * "icvm617": {
     *     "0": "2021-08-18T00:00",
     *     "1": "2021-08-24T00:00",
     *     "2": "2021-08-17T00:00",
     *     "3": "2021-09-01T00:00",
     *     "4": "2021-09-10T00:00",
     *     "5": "2021-09-15T00:00",
     *     "6": "2021-09-20T00:00",
     *     "7": "2021-09-25T00:00",
     *     "8": "2021-09-30T00:00",
     *     "9": "2021-10-10T00:00"
     *   }
     */
    private readonly icvm617Settings: {
        0: '',
        1: '',
        2: '',
        3: '',
        4: '',
        5: '',
        6: '',
        7: '',
        8: '',
        9: '',
    };

    private readonly user$: Observable<User>;

    private userViewedNotification = false;

    private userCblc: number;

    constructor(
        private store: Store<AppState>,
        private settings: SettingsService) {
            this.icvm617Settings = this.settings.getConfigurationKeyValue('icvm617');

            this.user$ = this.store.select((s) => s.userProfile.user);

            this.store.select((state) => state.authentication)
            .subscribe((auth) => {
                if (auth.currentUser === null) return;
                const userAuth = { ...auth };
                this.userCblc = userAuth.currentUser.cblc;
            });
    }

    /**
     * Retorna a data de expiração do usuário de acordo com configuração.
     * @returns user expiration date
     */
    public getUserExpirationDate(): Observable<Date> {
        return this.user$
            .pipe(
                filter((user) => !!user && !!user.riskType),
                first(),
                map((user) => {
                    return this.calculateExpirationDate(this.userCblc);
                }),
            );
    }

    /**
     * Verifica se o usuário deve ser informado sobre a necessidade de atualização dos dados.
     * Somente é apresetado se o usuário ainda não visualizou uma notificação.
     * @returns show notification
     */
    public mustShowNotification(): Observable<{mustShowNotification: boolean, userBlocked: boolean}> {
        return this.user$.pipe(
                filter((user) =>  !!user && !!user.riskType),
                first(),
                map((user) => {
                    if (this.userViewedNotification === false && this.spouseCpfOrCompanyCnpjIsOutOfDate(user)) {
                        const expirationDate = this.calculateExpirationDate(this.userCblc);

                        return { mustShowNotification: true, userBlocked: this.deadlineExceeded(expirationDate) };
                    }

                    return { mustShowNotification: false, userBlocked: false };
                }),
            );
    }

    /**
     * Verifica se o usuário está bloqueado
     * @returns is blocked
     */
    public userIsBlocked(): Observable<boolean> {
        return this.user$.pipe(
                filter((user) => !!user),
                first(),
                timeout(5000),
                map((user) => {
                    if (!user.riskType || !this.userCblc) {
                        return false;
                    }

                    if (this.spouseCpfOrCompanyCnpjIsOutOfDate(user)) {
                        const expirationDate = this.calculateExpirationDate(this.userCblc);

                        return this.deadlineExceeded(expirationDate);
                    }

                    return false;
                }),
                catchError((error) => {
                    console.error('[Icvm617Service] userIsBlocked error:', error);
                    return of(false);
                }),
            );
    }

    /**
     *  Verifica se o usuário deve atualizar seus dados.
     * @returns must update icvm617 data
     */
    public userMustUpdateIcvm617Data(): Observable<boolean> {
        return this.user$.pipe(
            filter((user) => !!user && !!user.riskType),
            first(),
            map((user) => {
                return this.spouseCpfOrCompanyCnpjIsOutOfDate(user);
            }),
        );
    }

    public setUserViewedNotification() {
        this.userViewedNotification = true;
    }

    private spouseCpfOrCompanyCnpjIsOutOfDate(user: User): boolean {
        return ((this.isMarried(user) && !user.spouseCPF) || (!!user.isEmployed && !user.companyCNPJ)) &&
                (user.profile === RolesUser.Registered || user.profile === RolesUser.Activated);
    }

    private isMarried(user: User): boolean {
        return this.maritalStatusForVerification.some((status) => status === user.maritalStatusID);
    }

    private calculateExpirationDate(cblc: number): Date {
        // tslint:disable-next-line: newline-per-chained-call
        const lastNumberOfCblc = +cblc.toString().split('').pop();

        const date = this.icvm617Settings[lastNumberOfCblc];

        return new Date(date);
    }

    private deadlineExceeded(expirationDate: Date) {
        const today = new Date(new Date().toDateString());
        return (today > expirationDate);
    }
}
